import { useEffect, useRef } from "react";

// Served same-origin from the dashboard build (dashboard/public/hex-grid,
// copied by rspack.config.ts) because the /display CSP allows only 'self'.
const HEX_GRID_SCRIPT = "/hex-grid/hex-grid.js";

// Tuned for always-on, low-end wall phones: a capped frame rate keeps the
// grid alive (moving pixels against burn-in) without running the GPU at
// display refresh rate all day. Pointer glow is off — nobody hovers a wall.
const HEX_GRID_OPTIONS = {
  fps: 15,
  clusters: 4,
  flexZones: 2,
  mouse: false,
  exclude: ".dd-panel",
  blueFaces: "/hex-grid/faces/blue/face-{n}.jpg",
  blueFaceCount: 25,
  orangeFaces: "/hex-grid/faces/orange/face-{n}.jpg",
  orangeFaceCount: 27
} as const;

interface HexGridHandle {
  destroy: () => void;
}

interface HexGridApi {
  attach: (el: HTMLElement, opts: Record<string, unknown>) => HexGridHandle;
}

declare global {
  interface Window {
    HexGrid?: HexGridApi;
  }
}

let scriptLoad: Promise<HexGridApi | null> | null = null;

function loadHexGrid(): Promise<HexGridApi | null> {
  if (window.HexGrid) return Promise.resolve(window.HexGrid);
  if (!scriptLoad) {
    scriptLoad = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = HEX_GRID_SCRIPT;
      script.async = true;
      script.onload = () => resolve(window.HexGrid ?? null);
      script.onerror = () => {
        script.remove();
        scriptLoad = null;
        resolve(null);
      };
      document.head.appendChild(script);
    });
  }
  return scriptLoad;
}

/**
 * Living hex grid backdrop. Purely decorative: if the script or canvas is
 * unavailable the page keeps its plain dark background.
 */
export default function HexGridBackground({ className }: { className: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let disposed = false;
    let handle: HexGridHandle | null = null;
    void loadHexGrid().then((api) => {
      if (disposed || !api || !ref.current) return;
      try {
        handle = api.attach(ref.current, HEX_GRID_OPTIONS);
      } catch {
        handle = null;
      }
    });
    return () => {
      disposed = true;
      handle?.destroy();
    };
  }, []);

  return <div ref={ref} className={className} aria-hidden="true" />;
}
